import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, TrendingUp, TrendingDown, Clock, Brain, RefreshCw } from 'lucide-react';
import { useAuthStore } from '@shared/state';
import { getRecalibrationHistory, isDiagnosticRetakeSuggested } from '@shared/services/adaptive';
import type { RecalibrationHistory } from '@shared/types/adaptive';
import { DIMENSION_DISPLAY } from '@shared/types/cogniprint';
import { Button } from '../components/ui/Button';
import { RidgeTexture } from '../components/ui/RidgeTexture';
import { THICK_TILE_STYLE } from '../components/dashboard/tileStyles';

function getDimensionColour(dimKey: string): string {
  return DIMENSION_DISPLAY.find((d) => d.key === dimKey)?.colour ?? '#6C5CE7';
}

function getDimensionName(dimKey: string): string {
  return DIMENSION_DISPLAY.find((d) => d.key === dimKey)?.name ?? dimKey;
}

export function ProfileEvolutionPage() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const [history, setHistory] = useState<RecalibrationHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [retakeSuggested, setRetakeSuggested] = useState(false);

  useEffect(() => {
    if (!user) { navigate('/login'); return; }

    Promise.all([
      getRecalibrationHistory(user.uid, 20),
      isDiagnosticRetakeSuggested(user.uid),
    ]).then(([hist, retake]) => {
      setHistory(hist);
      setRetakeSuggested(retake);
    }).finally(() => setLoading(false));
  }, [user, navigate]);

  if (!user) return null;

  return (
    <div className="min-h-screen bg-dark-bg relative">
      <RidgeTexture opacity="opacity-[0.03]" />

      <div className="relative z-10 max-w-2xl mx-auto px-4 pt-32 pb-16">
        <Link to="/profile" className="inline-flex items-center gap-1 text-content-muted text-sm font-body mb-6 hover:text-content transition-colors">
          <ArrowLeft size={14} /> Back to Profile
        </Link>

        <motion.h1
          className="font-heading font-bold text-2xl sm:text-3xl mb-2"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          Profile Evolution
        </motion.h1>
        <div className="flex items-center justify-between mb-8">
          <p className="text-content-muted font-body text-sm">
            How your CogniPrint profile has evolved based on your learning behaviour.
          </p>
          <Link
            to="/profile/adaptations"
            className="text-xs text-indigo-400 hover:text-indigo-300 font-body inline-flex items-center gap-1 shrink-0 ml-4"
          >
            See detailed change log <ArrowRight size={10} />
          </Link>
        </div>

        {/* Diagnostic retake suggestion */}
        {retakeSuggested && (
          <motion.div
            className="relative overflow-hidden p-4 rounded-lg border border-gold-500/30 mb-8 flex items-center gap-3"
            style={THICK_TILE_STYLE}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <RefreshCw size={18} className="text-gold-400 shrink-0" />
            <div className="flex-1">
              <p className="text-sm text-content font-body">
                Your profile has shifted significantly from your original diagnostic. A retake would improve accuracy.
              </p>
            </div>
            <Button size="sm" onClick={() => navigate('/diagnostic?mode=retake')}>Retake</Button>
          </motion.div>
        )}

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : history.length === 0 ? (
          <motion.div
            className="relative overflow-hidden text-center py-12 rounded-lg border border-indigo-400/15"
            style={THICK_TILE_STYLE}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <Brain size={32} className="text-content-dim mx-auto mb-4" />
            <p className="text-content-secondary font-body text-sm mb-2">No profile evolution yet</p>
            <p className="text-content-dim font-body text-xs">
              Your profile will start evolving after you complete a few learning sessions. CogniPrint observes how you learn and fine-tunes your profile automatically.
            </p>
          </motion.div>
        ) : (
          <div className="space-y-4">
            {history.map((entry, i) => (
              <motion.div
                key={`${entry.version}-${entry.timestamp}`}
                className={`relative overflow-hidden rounded-lg border p-5 ${
                  entry.source === 'diagnostic' ? 'border-indigo-500/25' : 'border-gold-500/20'
                }`}
                style={THICK_TILE_STYLE}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
              >
                {/* Header */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-body font-medium text-content-muted bg-dark-subtle px-2 py-0.5 rounded-pill">
                      v{entry.version}
                    </span>
                    <span className={`text-xs font-body px-2 py-0.5 rounded-pill ${
                      entry.source === 'diagnostic'
                        ? 'bg-indigo-500/15 text-indigo-400'
                        : 'bg-gold-500/15 text-gold-400'
                    }`}>
                      {entry.source === 'diagnostic' ? 'Diagnostic' : 'Adaptive'}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 text-content-dim text-xs font-body">
                    <Clock size={12} />
                    {new Date(entry.timestamp).toLocaleDateString(undefined, {
                      month: 'short', day: 'numeric', year: 'numeric',
                    })}
                  </div>
                </div>

                {/* Dimension changes */}
                <div className="space-y-2">
                  {entry.changedDimensions.map((change) => {
                    const diff = change.after - change.before;
                    const isIncrease = diff > 0;

                    return (
                      <div key={change.dimension} className="flex items-center gap-3">
                        <div
                          className="w-2 h-2 rounded-full shrink-0"
                          style={{ backgroundColor: getDimensionColour(change.dimension) }}
                        />
                        <span className="text-sm text-content-secondary font-body flex-1">
                          {getDimensionName(change.dimension)}
                        </span>
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs text-content-dim font-body">{Math.round(change.before)}</span>
                          {isIncrease ? (
                            <TrendingUp size={14} className="text-success" />
                          ) : (
                            <TrendingDown size={14} className="text-warning" />
                          )}
                          <span className="text-xs text-content font-body font-medium">{Math.round(change.after)}</span>
                          <span className={`text-xs font-body font-medium ${isIncrease ? 'text-success' : 'text-warning'}`}>
                            ({isIncrease ? '+' : ''}{Math.round(diff)})
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Confidence delta */}
                {entry.confidenceDelta !== 0 && (
                  <p className="text-xs text-content-dim font-body mt-3">
                    Overall confidence {entry.confidenceDelta > 0 ? 'increased' : 'decreased'} by {Math.abs(entry.confidenceDelta).toFixed(2)}
                  </p>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
